import axios from 'axios';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import CaseDomains from './CaseDomains/CaseDomains.vue';
import RuleEngine from './RuleEngine/RuleEngine.vue';
import SlaWorkflows from './SlaWorkflows/SlaWorkflows.vue';
import RatesBilling from './RatesBilling/RatesBilling.vue';
import TenantIntegrations from './TenantIntegrations/TenantIntegrations.vue';
import BrandingCopy from '@/components/x-verdict/BrandingCopy/BrandingCopy.vue';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  GET_COMPANY_TYPES,
  get_request_config
} from '@/utils/system_constants/constant.js';
import { listXVerdictEntityConfigs } from '@/services/xverdict_entity_config_service';
import { mapEntityConfigToTenantDomainOption } from '@/services/xverdict_config_mapper';
import { advance_tenant_setup } from './tenant_settings_setup_helpers';
import {
  apply_saved_tenant_project,
  ensure_tenant_project_types,
  route_tenant_project_id,
  save_tenant_project,
  validate_tenant_project_fields
} from './tenant_settings_project_helpers';
import {
  create_tenant_setup_contact as create_setup_contact,
  ensure_tenant_setup_contact as ensure_setup_contact,
  find_tenant_setup_contact as find_setup_contact
} from './tenant_settings_contact_helpers';
import {
  create_tenant_settings_state,
  parse_api_data,
  tenant_setup_form_defaults
} from './tenant_settings_common_helpers';
import {
  tenant_setup_contact_name,
  validate_tenant_setup_organisation
} from './tenant_settings_validation_helpers';
import { tenant_role_methods } from './tenant_settings_role_methods';

async function fetch_tenant_setup_project_types() {
  return axios.get(GET_COMPANY_TYPES, get_request_config())
    .then((response) => tenant_setup_project_types(response))
    .catch(() => []);
}

function tenant_setup_project_types(response) {
  const response_data = parse_api_data(response.data);
  return Array.isArray(response_data.data) ? response_data.data : [];
}

export default {
  name: 'TenantSettings',
  components: {
    BlockingLoaderOverlay,
    CaseDomains,
    RuleEngine,
    SlaWorkflows,
    RatesBilling,
    TenantIntegrations,
    BrandingCopy
  },
  mixins: [alert_mixin],
  data: () => create_tenant_settings_state(),
  computed: {
    active_tab_label() {
      return this.tabs.find((tab) => tab.key === this.active_tab)?.label || 'This tab';
    },
    tenant_project_id() {
      return route_tenant_project_id(this.$route);
    },
    page_loading() {
      return this.roles_loading
        || this.people_loading
        || this.tenant_domains_loading
        || this.tenant_roles_loading
        || this.tenant_setup_creating_project
        || this.tenant_roles_saving;
    },
    page_loading_message() {
      if (this.tenant_setup_creating_project) return 'Creating tenant...';
      if (this.tenant_roles_saving) return 'Saving tenant roles...';
      if (this.tenant_domains_loading) return 'Loading case domains...';
      if (this.tenant_roles_loading) return 'Loading tenant roles...';
      return 'Loading tenant settings...';
    },
    page_loading_detail() {
      if (this.tenant_setup_creating_project) {
        return 'Provisioning the tenant project and setup data.';
      }

      if (this.tenant_roles_saving) {
        return 'Applying the selected roles to this tenant.';
      }

      return 'Fetching tenant roles, people, and setup options.';
    }
  },
  mounted() {
    this.load_roles_and_people();
    window.addEventListener('keydown', this.handle_tenant_setup_keydown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handle_tenant_setup_keydown);
  },
  methods: {
    ...tenant_role_methods,
    set_tab(tab_key) {
      this.active_tab = tab_key;
    },
    reset_tenant_setup() {
      this.tenant_setup_step = 1;
      this.tenant_setup_project_id = null;
      this.tenant_setup_contact_user_id = null;
      this.tenant_setup_contact_assigned = false;
      this.tenant_setup_project_types = [];
      this.tenant_setup_creating_project = false;
      this.tenant_setup_form = tenant_setup_form_defaults();
    },
    async open_tenant_setup_modal() {
      this.reset_tenant_setup();
      this.show_tenant_setup_modal = true;
      await Promise.all([
        this.load_tenant_setup_project_types(),
        this.load_tenant_domain_options(),
        this.load_tenant_role_options()
      ]);
    },
    close_tenant_setup_modal() {
      if (this.tenant_setup_creating_project) {
        return;
      }

      this.show_tenant_setup_modal = false;
      this.reset_tenant_setup();
    },
    handle_tenant_setup_keydown(event) {
      if (event.key === 'Escape' && this.show_tenant_setup_modal) {
        this.close_tenant_setup_modal();
      }
    },
    async next_tenant_setup_step() {
      await advance_tenant_setup(this);
    },
    previous_tenant_setup_step() {
      if (this.tenant_setup_step > 1) {
        this.tenant_setup_step -= 1;
      }
    },
    finish_tenant_setup() {
      this.close_tenant_setup_modal();
    },
    async load_tenant_setup_project_types() {
      this.tenant_setup_project_types = await fetch_tenant_setup_project_types();
    },
    async load_tenant_domain_options() {
      this.tenant_domains_loading = true;
      this.tenant_domains_error = '';

      try {
        const configs = await listXVerdictEntityConfigs({ is_active: true });
        this.tenant_domain_options = configs
          .map(mapEntityConfigToTenantDomainOption)
          .filter(Boolean);

        if (!this.tenant_domain_options.length) {
          this.tenant_domains_error = 'No entity types are available.';
        }
      } catch (error) {
        this.tenant_domain_options = [];
        this.tenant_domains_error = this.parse_error_message(
          error,
          'Entity types could not be loaded.'
        );
      } finally {
        this.tenant_domains_loading = false;
      }
    },
    tenant_setup_project_type_id() {
      const default_type = this.tenant_setup_project_types.find((type) =>
        String(type.project_type || '').toLowerCase().includes('default')
      );
      const selected_type = default_type || this.tenant_setup_project_types[0];
      const project_type_id = Number(selected_type?.id);

      return Number.isInteger(project_type_id) && project_type_id > 0
        ? project_type_id
        : null;
    },
    tenant_setup_project_description() {
      return `Sector: ${String(this.tenant_setup_form.sector || '').trim()}`;
    },
    tenant_setup_contact_name() {
      return tenant_setup_contact_name(this.tenant_setup_form);
    },
    validate_tenant_setup_organisation() {
      return validate_tenant_setup_organisation(this);
    },
    async find_tenant_setup_contact() {
      return find_setup_contact(this);
    },
    async create_tenant_setup_contact() {
      return create_setup_contact(this);
    },
    async ensure_tenant_setup_contact() {
      await ensure_setup_contact(this);
    },
    async save_tenant_setup_project() {
      await ensure_tenant_project_types(this);
      const project_type_id = validate_tenant_project_fields(this);
      if (!project_type_id) {
        return false;
      }

      this.tenant_setup_creating_project = true;

      try {
        const response_data = await save_tenant_project(this, project_type_id);
        return apply_saved_tenant_project(this, response_data);
      } catch (error) {
        return handle_tenant_project_save_error(this, error);
      } finally {
        this.tenant_setup_creating_project = false;
      }
    },
    request_params(extra = {}) {
      return tenant_request_params(this.tenant_project_id, extra);
    },
    parse_error_message(error, fallback) {
      const response_data = parse_api_data(error?.response?.data);

      return response_data.message || response_data.data || error.message || fallback;
    },
    parse_api_data(payload) {
      return parse_api_data(payload);
    }
  }
};

function handle_tenant_project_save_error(component, error) {
  component.show_error(component.parse_error_message(error, 'Tenant project could not be saved.'));
  return false;
}

function tenant_request_params(tenant_project_id, extra = {}) {
  const params = Object.assign(Object.fromEntries([]), extra);
  if (tenant_project_id) params.project_id = tenant_project_id;
  return params;
}
